import {
  seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResults,
  seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as SEOSegmentationCaregiverSearchResult,
  seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_providerSearchResults as SEOSegmentationProviderSearchResult,
  seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_providerSearchResults_latestReview as LatestReview,
} from '@/__generated__/seoSegmentationListingPage';
import { SearchDirectoryTemplateType } from '@/constants';
import { type ReviewCardProps } from './ReviewCard';

type CaregiverSearchResultsWithLatestReview = Omit<
  SEOSegmentationCaregiverSearchResult,
  'latestReview'
> & {
  latestReview: NonNullable<SEOSegmentationCaregiverSearchResult['latestReview']>;
};

type ProviderSearchResultsWithLatestReview = Omit<
  SEOSegmentationProviderSearchResult,
  'latestReview'
> & {
  latestReview: NonNullable<SEOSegmentationProviderSearchResult['latestReview']>;
};

export const useSearchResultReviews = (
  seoSegmentationSearchResults: SEOSegmentationSearchResults,
  searchDirectoryTemplateType: SearchDirectoryTemplateType,
  maxNumberOfReviewsToDisplay: number = 5
): ReviewCardProps[] | null => {
  const { caregiverSearchResults, providerSearchResults } = seoSegmentationSearchResults;

  const filterProfilesWithReviews = <T extends { latestReview: LatestReview | null }>(
    profiles: T[]
  ): Array<Omit<T, 'latestReview'> & { latestReview: NonNullable<T['latestReview']> }> => {
    return profiles
      .filter(
        (profile) =>
          profile.latestReview && profile.latestReview.comment && profile.latestReview.rating
      )
      .map((profile) => ({
        ...profile,
        latestReview: profile.latestReview as NonNullable<T['latestReview']>, // Explicitly cast to non-nullable
      }));
  };

  const mapCaregiverReviewCardProps = (
    profiles: CaregiverSearchResultsWithLatestReview[]
  ): ReviewCardProps[] => {
    return profiles.map(({ latestReview: { reviewer, comment, rating }, caregiver }) => ({
      revieweeName: caregiver.member.displayName,
      reviewerName: reviewer.displayName !== '' ? reviewer.displayName : 'Care member',
      comment,
      rating,
    }));
  };

  const mapProviderReviewCardProps = (
    profiles: ProviderSearchResultsWithLatestReview[]
  ): ReviewCardProps[] => {
    return profiles.map(
      ({ latestReview: { reviewer, comment, rating }, seoSegmentationProvider }) => ({
        revieweeName: seoSegmentationProvider.provider.name,
        reviewerName: reviewer.displayName !== '' ? reviewer.displayName : 'Care member',
        comment,
        rating,
      })
    );
  };

  if (searchDirectoryTemplateType === 'CAREGIVER') {
    const caregiverProfiles = filterProfilesWithReviews(caregiverSearchResults);
    if (caregiverProfiles.length < 3) return null;

    return mapCaregiverReviewCardProps(caregiverProfiles).slice(0, maxNumberOfReviewsToDisplay);
  }
  if (searchDirectoryTemplateType === 'SMB') {
    const providerProfiles = filterProfilesWithReviews(providerSearchResults);
    if (providerProfiles.length < 3) return null;

    return mapProviderReviewCardProps(providerProfiles).slice(0, maxNumberOfReviewsToDisplay);
  }
  if (searchDirectoryTemplateType === 'HYBRID') {
    const caregiverProfiles = filterProfilesWithReviews(caregiverSearchResults);
    const providerProfiles = filterProfilesWithReviews(providerSearchResults);

    if (caregiverProfiles.length + providerProfiles.length < 3) {
      return null;
    }

    return [
      ...mapCaregiverReviewCardProps(caregiverProfiles),
      ...mapProviderReviewCardProps(providerProfiles),
    ].slice(0, maxNumberOfReviewsToDisplay);
  }

  return null;
};
